import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Image from './image.jsx';
import Lightbox, {ImageSlide} from 'yet-another-react-lightbox';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import toast from "react-hot-toast";
import {usePage} from '@inertiajs/react';
import {Zoom} from 'yet-another-react-lightbox/plugins';
import Video from './video';
import {useTranslation} from 'react-i18next';
import VideoSlide from './videoSlide.jsx';

function isDesktop() {
    // Prefer Client Hints (most reliable)
    if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
        return !navigator.userAgentData.mobile;
    }
    // Fallback heuristics
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const isIPadOS = platform === 'MacIntel' && navigator.maxTouchPoints > 1; // iPadOS masquerades as Mac
    const isMobileUA = /Android|iPhone|iPod|iPad|Mobile/i.test(ua);
    return !isMobileUA && !isIPadOS;
}

const plugins = [Fullscreen, Zoom];

function Album({photos, aspectRatio, event, reloadUrl, nextCursor: initialNextCursor = null, hasMore: initialHasMore = false, effect, participant}) {
    const {auth} = usePage().props
    const [pictures, setPictures] = useState(photos);
    const [index, setIndex] = useState(-1);
    const [nextCursor, setNextCursor] = useState(initialNextCursor);
    const [hasMore, setHasMore] = useState(initialHasMore);
    const [loadingMore, setLoadingMore] = useState(false);
    const sentinelRef = useRef(null);
    const {t} = useTranslation();

    useEffect(() => {
        setPictures(photos); // reload hook when switching disposable
        setNextCursor(initialNextCursor);
        setHasMore(initialHasMore);
    }, [photos]);

    const images = useMemo(() => {
        return pictures.map(p => {

            if (p.file_type === 'video') {
                return {
                    ...p,
                    type: 'video',
                    playHls: p.playHls,
                    description: p.timestamp,
                    descriptionTextAlign: 'center',
                    canDelete: !!p.user && p.user.name === auth?.user?.name,
                    title: p.user.name + ' @ ' + event.name,
                }
            }
            return {
                type: 'photo',
                src: p.url,
                previewUrl: p.previewUrl,
                shareUrl: p.shareUrl,
                gallery_cache_key: p.gallery_cache_key,
                title: p.user.name + ' @ ' + event.name,
                description: p.timestamp,
                descriptionTextAlign: 'center',
                blurhash: p.blurhash,
                id: p.id,
                canDelete: !!p.user && p.user.name === auth?.user?.name,
            }
        })
    }, [pictures.length, event, auth?.user]);

    const loadMore = useCallback(async () => {
        if (loadingMore || !hasMore || !nextCursor) {
            return;
        }
        setLoadingMore(true);
        try {
            const {data} = await axios.get(reloadUrl, {params: {cursor: nextCursor, effect_id: effect, user_id: participant || undefined}});
            setPictures(prev => {
                const seen = new Set(prev.map(p => `${p.file_type}-${p.id}`));
                const fresh = data.data.filter(p => !seen.has(`${p.file_type}-${p.id}`));
                return [...prev, ...fresh];
            });
            setNextCursor(data.next_cursor);
            setHasMore(data.has_more);
        } catch (e) {
            console.warn(e);
        } finally {
            setLoadingMore(false);
        }
    }, [reloadUrl, nextCursor, hasMore, loadingMore, effect, participant]);

    useEffect(() => {
        if (!sentinelRef.current || !hasMore) {
            return;
        }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                loadMore();
            }
        }, {rootMargin: '400px'});
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [loadMore, hasMore]);

    const saveSlide = useCallback(async (slide) => {
        const isVideo = slide.type === 'video';
        const fileName = (slide.gallery_cache_key ?? 'photo') + (isVideo ? '.mp4' : '.jpg');

        const downloadBlob = (blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        };

        // Last-resort fallback: navigate to URL so user can long-press to save.
        // Use location.href instead of window.open — in-app webviews (Instagram, WhatsApp) block popups.
        const openUrl = () => {
            if (!slide.shareUrl) {
                toast.error(t('Could not save. Please try again later.'));
                return;
            }
            location.href = slide.shareUrl;
            toast(t('Long-press the image to save it.'), {duration: 5000});
        };

        const toastId = toast.loading(t('Preparing...'));
        let blob;
        try {
            const res = await fetch(slide.shareUrl, {cache: 'no-cache'});
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            blob = await res.blob();
        } catch {
            toast.dismiss(toastId);
            openUrl();
            return;
        }
        toast.dismiss(toastId);

        if (isDesktop()) {
            downloadBlob(blob);
            return;
        }

        // Mobile: prefer Web Share API with file (saves to camera roll on iOS/Android)
        const file = new File([blob], fileName, {type: isVideo ? 'video/mp4' : 'image/jpeg'});
        const shareData = {files: [file]};
        if (navigator.share && navigator.canShare?.(shareData)) {
            try {
                await navigator.share(shareData);
                return;
            } catch (e) {
                // AbortError = user dismissed the share sheet — not an error
                if (e.name === 'AbortError') {
                    return;
                }
            }
        }

        // Fallback: blob anchor download works on Android Chrome even when share API is unavailable.
        // On iOS Safari, <a download> is ignored for blobs, so openUrl() is the final fallback.
        try {
            downloadBlob(blob);
        } catch {
            openUrl();
        }
    }, [t]);

    const deleteSlide = useCallback(async (slide) => {
        if (!confirm(t('are you sure you want to delete this {{captureType}}?', {captureType: slide.type}))) {
            return;
        }
        try {
            await axios.delete(`/camera/${event.id}/${slide.type}s/${slide.id}`);
            setPictures(pictures.filter(p => !(p.id === slide.id && p.file_type === slide.type)))
            toast.success(t('{{captureType}} deleted', {captureType: slide.type}));
        } catch (e) {
            toast.error(t('Could not delete this {{captureType}}.', {captureType: slide.type}))
        } finally {
            setIndex(-1);

        }
    }, [event, pictures]);

    return (
        <div className="flex flex-col grow h-full">
            {images.length > 0 &&
                <div className="grid gap-4 grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 pb-24">
                    {images.map((p, i) => {
                        if (p.type === 'video' && !p.previewUrl) {
                            return <Video
                                aspectRatio={aspectRatio}
                                key={`${p.type}-${p.id}`}
                                video={p}
                                onClick={() => {
                                    setIndex(i);
                                }} />
                        }

                        return (
                            <Image aspectRatio={aspectRatio}
                                   recordDuration={p.type === 'video' ? p.duration_formatted : null}
                                   key={`${p.type}-${p.id}`}
                                   url={p.previewUrl}
                                   blurhash={p.blurhash}
                                   alt={p.title}
                                   onClick={() => {
                                       setIndex(i);
                                   }} />)

                    })}
                    {loadingMore && Array.from({length: 6}).map((_, i) => (
                        <div key={`skeleton-${i}`} className="rounded-[20px] overflow-hidden opacity-40" style={{aspectRatio}}>
                            <div className="w-full h-full animate-pulse bg-white/10" />
                        </div>
                    ))}
                </div>}
            {images.length === 0 && (
                <div className="button-backdrop flex items-center justify-center mt-2 p-4">
                    <div className="text-white-foreground text-center text-sm">
                        {t('No photos yet. Once photos are taken, the gallery will appear here.')}
                    </div>
                </div>
            )}
            {hasMore && <div ref={sentinelRef} className="h-1" />}

            <Lightbox
                toolbar={{
                    buttons: [
                        'fullscreen',
                        'close',
                    ],
                }}
                render={{
                    slide: ({slide, offset}) =>
                        slide.type === "video" ? (
                            <VideoSlide isPlaying={offset === 0} slide={slide} />
                        ) : <ImageSlide slide={slide} />,
                    slideFooter: ({slide}) => {
                        return (
                            <>
                                <button onClick={() => saveSlide(slide)}
                                        className={
                                            `md:hidden focus:border-purple-500 flex justify-center items-center text-white-foreground absolute ${slide.canDelete ? 'right-[20px]' : ''} bottom-[20px] bg-back/70 backdrop-blur-sm border-[0.5px] border-[#2E2E2E] rounded-full px-4 py-2`
                                        }>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                         strokeWidth="1.5"
                                         stroke="currentColor" className="size-6 mr-2">
                                        <path strokeLinecap="round" strokeLinejoin="round"
                                              d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                    </svg>
                                    {t('Save {{captureType}}', {captureType: slide.type})}
                                </button>
                                {slide.canDelete && <button onClick={() => deleteSlide(slide)}
                                                            className="md:hidden focus:border-purple-500 flex justify-center left-[20px] items-center text-red-500 absolute bottom-[20px] bg-back/70 backdrop-blur-sm border-[0.5px] border-[#2E2E2E] rounded-full px-4 py-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                         strokeWidth="1.5" stroke="currentColor" className="size-6">
                                        <path strokeLinecap="round" strokeLinejoin="round"
                                              d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                                    </svg>
                                    {t('Delete {{captureType}}', {captureType: slide.type})}
                                </button>}
                            </>
                        )
                    }
                }}
                on={{
                    view: ({index: currentIndex}) => {
                        setIndex(currentIndex);
                    },
                }}
                carousel={{
                    imageFit: 'contain'
                }}
                slides={images}
                open={index >= 0}
                index={index}
                draggable={false}
                close={() => setIndex(-1)}
                // enable optional lightbox plugins
                plugins={plugins}
            />
            {/* carousel zindex is 9999 */}
        </div>
    );
}

export default Album;
